/**
 * Shared list frame for My Work and Reviews: URL-backed page number, the
 * truthful loading / error / denied / empty states, a row list, and a pager
 * driven by the server's meta (never by the length of the current page).
 */
import React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { getErrorMessage } from '../../packages/api-client';
import { useAuth } from '../../packages/auth-client';
import type { WorkItem, WorkPage } from '../../packages/api-client/work-api';
import { StateView } from '../../packages/ui';
import { formatDueDate, isDueDateOverdue } from '../../utils/date';
import './Work.css';

const STATUS_LABEL: Record<string, string> = {
    todo: 'To do', in_progress: 'In progress', review: 'In review', blocked: 'Blocked', done: 'Done',
};

export function WorkList<T extends WorkItem>({ queryKey, fetchPage, label, emptyTitle, emptyMessage, renderExtra }: {
    queryKey: string;
    fetchPage: (page: number) => Promise<WorkPage<T>>;
    /** Accessible name of the list. */
    label: string;
    emptyTitle: string;
    emptyMessage: React.ReactNode;
    /** Page-specific content under a row's title line. */
    renderExtra?: (item: T) => React.ReactNode;
}) {
    // A restricted viewer may see a task without being able to open its
    // project (a task-only link), so the project is plain text for them.
    const linkProjects = !useAuth().isRestricted();
    const [params, setParams] = useSearchParams();
    const raw = Number(params.get('page'));
    const page = Number.isInteger(raw) && raw > 0 ? raw : 1;
    const q = useQuery({
        queryKey: [queryKey, page],
        queryFn: () => fetchPage(page),
        placeholderData: keepPreviousData,
    });

    const goTo = (p: number) => {
        const next = new URLSearchParams(params);
        if (p <= 1) next.delete('page'); else next.set('page', String(p));
        setParams(next);
    };

    if (q.isPending) return <StateView state="loading" label={`Loading ${label.toLowerCase()}`} lines={4} />;
    if (q.isError) {
        if (isAxiosError(q.error) && q.error.response?.status === 403) {
            return <StateView state="denied" reason="Your role does not include reading tasks."
                nextStep="Ask a workspace admin for task access." />;
        }
        return <StateView state="error" message={getErrorMessage(q.error)} onRetry={() => q.refetch()} retrying={q.isFetching} />;
    }

    const { data, meta } = q.data;
    if (meta.total === 0) return <StateView state="empty" title={emptyTitle} message={emptyMessage} />;
    if (data.length === 0) {
        // A stale ?page= past the end: say so and offer the first page.
        return <StateView state="empty" title={`Page ${page} is empty`}
            message={`There are ${meta.total} tasks on ${meta.total_pages} page${meta.total_pages === 1 ? '' : 's'}.`}
            action={<button type="button" className="btn btn-secondary btn-md" onClick={() => goTo(1)}>Go to page 1</button>} />;
    }

    return (
        <div className="work-list" aria-busy={q.isFetching || undefined}>
            <ul aria-label={label}>
                {data.map((item) => (
                    <li key={item.id} className="work-row">
                        <div className="work-row-main">
                            <Link to={`/tasks/${item.id}`} className="work-row-title">{item.title}</Link>
                            {linkProjects
                                ? <Link to={`/projects/${item.project_id}`} className="work-row-project">{item.project_name}</Link>
                                : <span className="work-row-project">{item.project_name}</span>}
                        </div>
                        <div className="work-row-meta">
                            <span className={`status-badge status-${item.status}`}>{STATUS_LABEL[item.status] ?? item.status}</span>
                            {item.priority && item.priority !== 'none' && (
                                <span className={`priority-badge priority-${item.priority}`}>Priority: {item.priority}</span>
                            )}
                            <Due iso={item.due_date} />
                            <Readiness item={item} />
                        </div>
                        {renderExtra?.(item)}
                    </li>
                ))}
            </ul>
            <nav className="work-pager" aria-label="Pagination">
                <button type="button" className="btn btn-ghost btn-sm" aria-label="Previous page"
                    onClick={() => goTo(page - 1)} disabled={page <= 1}><ChevronLeft size={16} aria-hidden="true" /></button>
                <span>Page {meta.page} of {Math.max(meta.total_pages, 1)} · {meta.total} task{meta.total === 1 ? '' : 's'}</span>
                <button type="button" className="btn btn-ghost btn-sm" aria-label="Next page"
                    onClick={() => goTo(page + 1)} disabled={page >= meta.total_pages}><ChevronRight size={16} aria-hidden="true" /></button>
            </nav>
        </div>
    );
}

function Due({ iso }: { iso?: string }) {
    if (!iso) return <span className="work-due work-due--none">No due date</span>;
    const overdue = isDueDateOverdue(iso);
    return (
        <span className={`work-due ${overdue ? 'work-due--overdue' : ''}`}>
            {overdue ? 'Overdue · ' : 'Due '}{formatDueDate(iso, { month: 'short', day: 'numeric', year: 'numeric' })}
        </span>
    );
}

/** Blocked: status blocked or an open prerequisite. Ready: to do with none open. */
function Readiness({ item }: { item: WorkItem }) {
    if (item.open_blockers > 0) {
        return <span className="work-flag work-flag--blocked">Waiting on {item.open_blockers} prerequisite{item.open_blockers === 1 ? '' : 's'}</span>;
    }
    if (item.status === 'todo') return <span className="work-flag work-flag--ready">Ready to start</span>;
    return null;
}
